import { useState, useEffect, useRef } from "react";
import { format, addDays } from "date-fns";
import { useSelector } from "react-redux";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_COURT_GRID_SCHEDULE_V2 } from "../../../api/apiRoutes";
import { State } from "../../../store";
import { CourtGridData } from "../../../models/CourtGridData";

interface UseWeeklyDataProps {
  startDate: Date;
  isMobile: boolean;
  courtName: string;
}

export default function useWeeklyData({ startDate, isMobile, courtName }: UseWeeklyDataProps) {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const userData = useSelector((state: State) => state.auth.userData);
  const isMountedRef = useRef(true);

  const [weeklyGridData, setWeeklyGridData] = useState<CourtGridData[]>([]);
  const [loading, setLoading] = useState(false);

  const requestWeeklyGridSchedule = async () => {
    if (!facilityIds || !courtName || !isMountedRef.current) return;

    if (isMountedRef.current) {
      setLoading(true);
    }

    // Oblicz zakres dat
    const dateFrom = format(startDate, "yyyy-MM-dd");
    const dateTo = isMobile ? dateFrom : format(addDays(startDate, 6), "yyyy-MM-dd");

    const userIds = userData?.objectId ? `0,${userData.objectId}` : undefined;

    const { result, isError } = await get(
      GET_COURT_GRID_SCHEDULE_V2(facilityIds, dateFrom, dateTo, courtName, userIds)
    );

    // Sprawdź czy komponent jest nadal zamontowany przed aktualizacją stanu
    if (!isMountedRef.current) return;

    if (!isError && Array.isArray(result)) {
      setWeeklyGridData(result);
    } else {
      setWeeklyGridData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    requestWeeklyGridSchedule();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, facilityIds, courtName, isMobile]);

  // Cleanup function - oznacz komponent jako odmontowany
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    weeklyGridData,
    loading,
    refetch: requestWeeklyGridSchedule,
  };
}

